import React, { useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  TermsAgreementLabel,
  Text,
  TextField,
} from "@merge-api/merge-javascript-shared";
import { AuthLayout } from "../AuthLayouts";
import { fetchWithoutAuth, FormErrorData, UserSuccessData } from "../../../../api-client/APIClient";
import { SignUpUserData } from "./SignUpScreen";
import { TERMS_REQUIRED_MESSAGE } from "./constants";
import { showErrorToast } from "../../../shared/Toasts";
import useSignupTelemetryIds from "./hooks/useSignupTelemetryIds";
import { ORGANIZATION_NAME_MAX_LENGTH } from "../../../../constants";
import { SignupIntent } from "../../../shared/utils/signupIntent";

interface Props {
  email: string;
  handleSuccessfulSignUp: (data: UserSuccessData, formId: string) => void;
  idToken: string;
  inviteToken?: string | null;
  name?: string | null;
  organizationName?: string | null;
  socialAuthProvider: string;
  signupIntent?: SignupIntent;
}

const FinishSocialSignUp = ({
  email,
  handleSuccessfulSignUp,
  idToken,
  inviteToken,
  name,
  organizationName,
  socialAuthProvider,
  signupIntent,
}: Props) => {
  const [nameValue, setNameValue] = useState<string>(name ?? "");
  const [organizationValue, setOrganizationValue] = useState<string>(organizationName ?? "");
  const [agreedToTerms, setAgreedToTerms] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    signupFormGTag,
    createAccountButtonGTag,
    successFormId: hubspotFormID,
  } = useSignupTelemetryIds({
    hasInviteToken: Boolean(inviteToken),
    isSamlRequired: false,
    socialAuthProvider,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;

    const errors: Record<string, string> = {};
    if (!nameValue.trim()) errors.name = "Please enter a name.";
    if (!organizationValue.trim()) errors.organization = "Please enter a company name.";
    if (!agreedToTerms)
      errors.agreed_to_terms_of_service_and_read_privacy_policy = TERMS_REQUIRED_MESSAGE;
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setIsLoading(true);

    const userInfo: SignUpUserData = {
      name: nameValue,
      organization: organizationValue,
      email,
      social_auth_provider: socialAuthProvider,
      agreed_to_terms_of_service_and_read_privacy_policy: agreedToTerms,
      id_token: idToken,
    };
    if (inviteToken) {
      userInfo.email_token = inviteToken;
    }
    if (signupIntent) {
      userInfo.signup_intent = signupIntent;
    }

    fetchWithoutAuth({
      path: "/users/create/social",
      method: "POST",
      body: userInfo,
      onResponse: (data: UserSuccessData) => {
        handleSuccessfulSignUp(data, hubspotFormID);
      },
      onError: (err) => {
        if (err) {
          err.json().then((data: FormErrorData) => {
            const newFieldErrors: Record<string, string> = {};
            for (const field in data) {
              if (field === "non_field_errors") {
                setErrorMessage(data[field][0]);
                continue;
              }
              if (["name", "organization"].includes(field)) {
                newFieldErrors[field] = data[field][0];
              }
            }
            if (Object.keys(newFieldErrors).length) setFieldErrors(newFieldErrors);
          });
        } else {
          showErrorToast("A network error has occurred. Please try again.");
        }
        setIsLoading(false);
      },
    });
  };

  return (
    <AuthLayout title="Set up your account" description="Add your profile details below">
      <form id={signupFormGTag} onSubmit={handleSubmit} noValidate className="flex flex-col gap-6">
        {errorMessage && <Alert color="red">{errorMessage}</Alert>}
        <TextField
          name="name"
          type="text"
          labelTitle="Full name"
          placeholder="Full name"
          value={nameValue}
          onChange={(e) => {
            setNameValue(e.target.value);
            if (fieldErrors.name)
              setFieldErrors((prev) => {
                const next = { ...prev };
                delete next.name;
                return next;
              });
          }}
          readOnly={Boolean(name)}
          disabled={Boolean(name)}
          error={!!fieldErrors.name}
          errorText={fieldErrors.name}
        />
        <TextField
          name="organization"
          type="text"
          labelTitle="Company"
          placeholder="Company"
          value={organizationValue}
          maxLength={ORGANIZATION_NAME_MAX_LENGTH}
          onChange={(e) => {
            setOrganizationValue(e.target.value);
            if (fieldErrors.organization)
              setFieldErrors((prev) => {
                const next = { ...prev };
                delete next.organization;
                return next;
              });
          }}
          readOnly={Boolean(inviteToken)}
          disabled={Boolean(inviteToken)}
          error={!!fieldErrors.organization}
          errorText={fieldErrors.organization}
        />
        <Checkbox
          variant="secondary"
          labelAlignment="start"
          className="[&_button]:!border-black [&_button[aria-checked='true']]:!bg-black"
          checked={agreedToTerms}
          onChange={(v) => {
            setAgreedToTerms(v);
            if (fieldErrors.agreed_to_terms_of_service_and_read_privacy_policy)
              setFieldErrors((prev) => {
                const next = { ...prev };
                delete next.agreed_to_terms_of_service_and_read_privacy_policy;
                return next;
              });
          }}
          label={<TermsAgreementLabel variant="sm" />}
        />
        {fieldErrors.agreed_to_terms_of_service_and_read_privacy_policy && (
          <Text variant="sm" className="text-red-60 -mt-2">
            {fieldErrors.agreed_to_terms_of_service_and_read_privacy_policy}
          </Text>
        )}
        <Button type="submit" fullWidth loading={isLoading} id={createAccountButtonGTag}>
          Next
        </Button>
      </form>
    </AuthLayout>
  );
};

export default FinishSocialSignUp;
